export type WorkspaceTemplateKind =
  | "kickoff"
  | "incident"
  | "race_ops"
  | "meeting";

export interface WorkspaceTemplateDefinition {
  icon: string;
  docTitle: string;
  parentTitlePattern: string | null;
  sections: string[];
  children: WorkspaceTemplateChildDefinition[];
}

export interface WorkspaceTemplateChildDefinition {
  icon: string;
  title: string;
  sections: string[];
}

export interface WorkspaceTemplateRecord {
  id: string;
  workspace_id: string;
  slug: string;
  name: string;
  template_kind: WorkspaceTemplateKind;
  description: string;
  definition: Record<string, unknown>;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

const KIND_DEFAULTS: Record<
  WorkspaceTemplateKind,
  WorkspaceTemplateDefinition
> = {
  kickoff: {
    icon: "🚀",
    docTitle: "Project kickoff",
    parentTitlePattern: null,
    sections: [
      "Outcome",
      "Why now",
      "Scope",
      "Owners",
      "Milestones",
      "Risks",
      "First actions",
    ],
    children: [],
  },
  incident: {
    icon: "🚨",
    docTitle: "Incident response",
    parentTitlePattern: null,
    sections: [
      "Impact",
      "Current status",
      "Timeline",
      "Containment",
      "Recovery",
      "Root cause",
      "Follow-ups",
    ],
    children: [],
  },
  race_ops: {
    icon: "🏁",
    docTitle: "Race operations",
    parentTitlePattern: null,
    sections: [
      "Event objective",
      "Schedule",
      "People",
      "Systems",
      "Checklists",
      "Risks",
      "Escalation",
    ],
    children: [],
  },
  meeting: {
    icon: "📅",
    docTitle: "Meeting",
    parentTitlePattern: "📅 <ISO-date> · <participants>",
    sections: ["Purpose", "Decisions", "Open questions", "Actions"],
    children: [
      {
        icon: "📓",
        title: "Notes",
        sections: ["Context", "Discussion", "Decisions", "Actions"],
      },
      {
        icon: "📜",
        title: "Transcript",
        sections: ["Transcript"],
      },
      {
        icon: "🎯",
        title: "Cheat Sheet",
        sections: ["Key points", "Decisions", "Actions"],
      },
      {
        icon: "❓",
        title: "QA",
        sections: ["Questions", "Answers"],
      },
      {
        icon: "🎁",
        title: "Prep",
        sections: ["Goals", "Agenda", "Background"],
      },
    ],
  },
};

const SECTION_ICONS: Record<string, string> = {
  outcome: "🎯",
  "why now": "🧭",
  scope: "📐",
  owners: "👥",
  people: "👥",
  milestones: "🗓️",
  timeline: "🕒",
  schedule: "🗓️",
  risks: "⚠️",
  escalation: "🚨",
  "first actions": "✅",
  actions: "✅",
  "follow-ups": "✅",
  impact: "💥",
  "current status": "📊",
  containment: "🛡️",
  recovery: "🔁",
  "root cause": "🔎",
  "event objective": "🏁",
  systems: "⚙️",
  checklists: "📋",
  purpose: "🧭",
  decisions: "⚖️",
  "open questions": "❓",
};

export function normalizeWorkspaceTemplate(
  record: WorkspaceTemplateRecord
): WorkspaceTemplateDefinition {
  const fallback = KIND_DEFAULTS[record.template_kind];
  const definition = record.definition;
  const icon =
    typeof definition.icon === "string" && definition.icon.trim()
      ? definition.icon.trim().slice(0, 8)
      : fallback.icon;
  const titleCandidate =
    typeof definition.doc_title === "string"
      ? definition.doc_title
      : typeof definition.parent_title_pattern === "string"
        ? fallback.docTitle
        : record.name;
  const docTitle = titleCandidate.trim().slice(0, 240) || fallback.docTitle;
  const parentTitlePattern =
    typeof definition.parent_title_pattern === "string" &&
    definition.parent_title_pattern.trim()
      ? cleanHeading(definition.parent_title_pattern, "").slice(0, 240) ||
        fallback.parentTitlePattern
      : fallback.parentTitlePattern;
  const sections = Array.isArray(definition.sections)
    ? definition.sections
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim().slice(0, 100))
        .filter(Boolean)
        .slice(0, 20)
    : [];
  const children = Array.isArray(definition.children)
    ? definition.children
        .map(normalizeTemplateChild)
        .filter(
          (value): value is WorkspaceTemplateChildDefinition => value !== null
        )
        .slice(0, 12)
    : [];

  return {
    icon,
    docTitle,
    parentTitlePattern,
    sections: sections.length ? sections : fallback.sections,
    children: children.length ? children : fallback.children,
  };
}

export function suggestedWorkspaceTemplateTitle(
  definition: WorkspaceTemplateDefinition,
  date = new Date()
): string {
  if (!definition.parentTitlePattern) return definition.docTitle;
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return definition.parentTitlePattern
    .replace("<ISO-date>", `${year}-${month}-${day}`)
    .replace("<participants>", "Participants");
}

export function renderTemplateMarkdown(input: {
  title: string;
  description?: string;
  sections: readonly string[];
}): string {
  const title = cleanHeading(input.title, "Untitled");
  const description = input.description?.trim();
  const sections = input.sections
    .map((section) => cleanHeading(section, "Section"))
    .filter(Boolean);

  const lines = [`# ${title}`];
  if (description) {
    lines.push("", `> 💡 ${description.replace(/\s+/g, " ")}`);
  }
  for (const section of sections) {
    const icon = SECTION_ICONS[section.toLocaleLowerCase()] ?? "📌";
    lines.push("", `## ${icon} ${section}`, "", sectionPrompt(section));
  }
  return `${lines.join("\n").trim()}\n`;
}

function cleanHeading(value: string, fallback: string): string {
  return (
    value
      .replace(/[\r\n#]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || fallback
  );
}

function normalizeTemplateChild(
  value: unknown
): WorkspaceTemplateChildDefinition | null {
  if (typeof value === "string") {
    const cleaned = cleanHeading(value, "").slice(0, 240);
    if (!cleaned) return null;
    const [firstToken = "", ...titleTokens] = cleaned.split(/\s+/);
    const firstCharacter = Array.from(firstToken)[0] ?? "";
    const hasLeadingIcon =
      titleTokens.length > 0 && /\p{Extended_Pictographic}/u.test(firstCharacter);
    const title = hasLeadingIcon ? titleTokens.join(" ") : cleaned;
    return {
      icon: hasLeadingIcon ? firstToken.slice(0, 8) : "📄",
      title,
      sections: defaultChildSections(title),
    };
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const title =
    typeof record.title === "string"
      ? cleanHeading(record.title, "").slice(0, 240)
      : "";
  if (!title) return null;
  const icon =
    typeof record.icon === "string" && record.icon.trim()
      ? record.icon.trim().slice(0, 8)
      : "📄";
  const sections = Array.isArray(record.sections)
    ? record.sections
        .filter((section): section is string => typeof section === "string")
        .map((section) => cleanHeading(section, "").slice(0, 100))
        .filter(Boolean)
        .slice(0, 20)
    : [];
  return {
    icon,
    title,
    sections: sections.length ? sections : defaultChildSections(title),
  };
}

function defaultChildSections(title: string): string[] {
  const key = title.toLocaleLowerCase();
  if (key === "notes") {
    return ["Context", "Discussion", "Decisions", "Actions"];
  }
  if (key === "transcript") return ["Transcript"];
  if (key === "cheat sheet") return ["Key points", "Decisions", "Actions"];
  if (key === "qa") return ["Questions", "Answers"];
  if (key === "prep") return ["Goals", "Agenda", "Background"];
  return ["Context"];
}

function sectionPrompt(section: string): string {
  const key = section.toLocaleLowerCase();
  if (key === "actions" || key === "first actions" || key === "follow-ups") {
    return "- [ ] Add an explicit action — Owner: — Due: —";
  }
  if (key === "decisions") {
    return "- Add the decision, rationale, and decision owner.";
  }
  if (key === "risks" || key === "escalation") {
    return "- ⚠️ Add the risk, impact, owner, and response.";
  }
  if (key === "checklists") {
    return "- [ ] Add a checkpoint — Owner: — Due: —";
  }
  return `> Add ${section.toLocaleLowerCase()} here.`;
}
